import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, track, heartbeat, fileUrl } from "../lib/api";

const SiteContext = createContext({ site: null, blocked: false });

const ensureMeta = (name, content) => {
  if (!content) return;
  let el = document.querySelector(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const injectIntegrations = (i) => {
  ensureMeta("google-site-verification", i.gsc_verification);
  ensureMeta("msvalidate.01", i.bing_verification);
  if (i.ga_id && !document.getElementById("ga-script")) {
    const s = document.createElement("script");
    s.id = "ga-script";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(i.ga_id)}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() { window.dataLayer.push(arguments); };
    window.gtag("js", new Date());
    window.gtag("config", i.ga_id);
  }
};

const applyFavicon = (url) => {
  if (!url) return;
  const href = fileUrl(url);
  document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((l) => {
    l.setAttribute("href", href);
    l.removeAttribute("type");
  });
};

const useTracking = (enabled) => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (enabled && !pathname.startsWith("/admin")) {
      track({ type: "visit", path: pathname });
      heartbeat();
    }
  }, [pathname, enabled]);
  useEffect(() => {
    if (!enabled) return;
    const id = setInterval(heartbeat, 25000);
    document.addEventListener("visibilitychange", heartbeat);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", heartbeat); };
  }, [enabled]);
  useEffect(() => {
    if (!enabled) return;
    const onClick = (e) => {
      if (window.location.pathname.startsWith("/admin")) return;
      const el = e.target.closest("a,button");
      if (!el) return;
      const target = el.getAttribute("data-testid") || el.textContent.trim().slice(0, 40);
      track({ type: "click", path: window.location.pathname, target, href: el.getAttribute("href") || "" });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [enabled]);
};

export const SiteProvider = ({ children }) => {
  const [site, setSite] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const isAdmin = window.location.pathname.startsWith("/admin");

  useEffect(() => {
    api.get("/public/site").then(({ data }) => {
      setSite(data);
      injectIntegrations(data.integrations);
      applyFavicon(data.branding?.favicon);
    }).catch((e) => { if (e.response?.status === 403) setBlocked(true); });
  }, []);

  useTracking(!blocked && !isAdmin);
  return <SiteContext.Provider value={{ site, blocked }}>{children}</SiteContext.Provider>;
};

export const useSite = () => useContext(SiteContext);
